import { useMemo } from 'react';

import {
  type DEFAULT_AD_FILTER_PARAMS,
  ENTITLEMENT_RISK_LIST_AD_FILTER,
} from '../../../../../../shared/constants';
import {
  useGetRisksMutation,
  useLazyGetBusinessProcessQuery,
  useLazyGetFunctionsQuery,
  useLazyGetRulesetsQuery,
} from '../../../../../ViolationsTab/data-layer/violationsTabApi';

interface UnwrappablePromise<T> extends Promise<T> {
  abort(): void;
  unwrap(): Promise<T>;
}

interface FilterItem {
  label: string;
  id: string;
  value?: string;
}

type QueryMap = Record<
  string,
  (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>
>;

const EMPTY_QUERY_FN: QueryMap = {
  EMPTY_QUERY_FN_KEY: () =>
    ({
      unwrap() {
        return Promise.resolve('empty_query_fn_run_for_query');
      },
    }) as UnwrappablePromise<unknown>,
};

/**
 * Custom hook to generate a query map for advanced filters.
 *
 * This hook retrieves query functions for different advanced filters and
 * returns a memoized object mapping filter keys to their corresponding query functions.
 * Includes wrapper functions for rulesets (returns label as value) and functions (returns id as value).
 *
 * @param {boolean} [strict=false] - If `true`, only includes the query function which has initial options;
 *                                   if `false`, includes all types of query functions.
 * @returns {Object} Query map where keys are filter values and values are query functions with custom transformations.
 */
const useGetQueryMap = (strict = false) => {
  const [fetchRiskFilters] = useGetRisksMutation();
  const [fetchBusinessProcess] = useLazyGetBusinessProcessQuery();
  const [fetchRulesetFilters] = useLazyGetRulesetsQuery();
  const [fetchFuncFilters] = useLazyGetFunctionsQuery();

  const queryMap = useMemo(() => {
    // Wrapper for rulesets to return label as value
    const fetchRulesetFiltersWrapper = (
      params: typeof DEFAULT_AD_FILTER_PARAMS
    ) => {
      const controller = fetchRulesetFilters(params);

      return {
        abort: () => controller.abort(),
        unwrap: async () => {
          const result = (await controller.unwrap()) as FilterItem[];

          return result.map((item: FilterItem) => ({
            ...item,
            value: item.label,
          }));
        },
      } as UnwrappablePromise<unknown>;
    };

    // Wrapper for functions to return id as value
    const fetchFuncFiltersWrapper = (
      params: typeof DEFAULT_AD_FILTER_PARAMS
    ) => {
      const controller = fetchFuncFilters(params);

      return {
        abort: () => controller.abort(),
        unwrap: async () => {
          const result = (await controller.unwrap()) as FilterItem[];

          return result.map((item: FilterItem) => ({
            ...item,
            value: item.id,
          }));
        },
      } as UnwrappablePromise<unknown>;
    };

    const queryFn = {
      [ENTITLEMENT_RISK_LIST_AD_FILTER.RULESET.value]:
        fetchRulesetFiltersWrapper,
    };
    const isEmptyQueryMap = Boolean(Object.keys(queryFn).length === 0);

    if (strict && isEmptyQueryMap) {
      return EMPTY_QUERY_FN;
    }

    if (strict) {
      return queryFn as QueryMap;
    }

    return {
      ...queryFn,
      [ENTITLEMENT_RISK_LIST_AD_FILTER.RISKNAME.value]: fetchRiskFilters,
      [ENTITLEMENT_RISK_LIST_AD_FILTER.BUSINESSPROCESS.value]:
        fetchBusinessProcess,
      [ENTITLEMENT_RISK_LIST_AD_FILTER.RULESET.value]:
        fetchRulesetFiltersWrapper,
      [ENTITLEMENT_RISK_LIST_AD_FILTER.FUNCTION.value]: fetchFuncFiltersWrapper,
    };
  }, [
    strict,
    fetchRiskFilters,
    fetchBusinessProcess,
    fetchRulesetFilters,
    fetchFuncFilters,
  ]);

  return queryMap;
};

export default useGetQueryMap;
